<template>
  <div class="legacy">
    <HeroLegacy :content="content.hero"></HeroLegacy>

    <div v-if="content.introDuoText" class="legacy__intro">
      <DuoText :content="content.introDuoText"></DuoText>
    </div>

    <div class="legacy__sticky-container">
      <section
        v-for="(section, index) in content.sections"
        :id="section.slug"
        :key="`section-${index}`"
        class="legacy__section pt-l pb-l"
      >
        <div class="wrapper">
          <h2 class="legacy__section__title">{{ section.title }}</h2>
        </div>

        <DuoGallery
          v-for="(duo, j) in section.content"
          :key="`cpt-${j}`"
          :content="duo"
        ></DuoGallery>
      </section>
    </div>

    <div>
      <GBaseline :content="content.todayTitle" />
      <DuoText :content="content.todayDuoText"></DuoText>
    </div>

    <FullwidthPicture
      :content="content.todayFullwidthPicture"
    ></FullwidthPicture>
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { onMounted } from 'vue'

import DuoGallery from '@/components/duo/Gallery.vue'
import DuoText from '@/components/duo/Text.vue'
import FullwidthPicture from '@/components/global/FullwidthPicture.vue'
import HeroLegacy from '@/components/hero/Legacy.vue'

gsap.registerPlugin(ScrollTrigger)

defineProps({
  content: {
    type: Object,
    required: true,
  },
})

const initAnimation = () => {
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: '.legacy__sticky-container',
      pin: '.legacy',
      start: 'top top',
      end: 'bottom top',
      scrub: 1,
      anticipatePin: 1,
      pinType: 'fixed',
      pinSpacing: true,
      ease: 'Power1.easeOut',
      onLeave: self => {
        self.disable()
      },
    },
  })

  tl.to(
    '.legacy__section .duo-gallery',
    {
      height: 0,
      padding: 0,
      opacity: 0,
      stagger: 0.5,
    },
    '<'
  )
    .to(
      '.legacy__section',
      {
        paddingTop: '4px',
        paddingBottom: '5px',
        stagger: 0.5,
      },
      '<'
    )
    .to(
      '.legacy__section .legacy__section__title',
      {
        fontSize: 30,
        stagger: 0.5,
      },
      '<'
    )
}

onMounted(() => {
  initAnimation()
})
</script>

<style lang="scss" scoped>
.legacy {
  :deep(.duo-gallery__content__headline) {
    line-height: 1;
  }
}

.legacy__intro {
  :deep(.duo-text) {
    margin: 0;
    padding: 7rem 0;
    color: $c-white;
    background-color: $c-green-dark;
  }
}

.legacy__sticky-container {
  position: relative;
}

.legacy__section__title {
  @extend %fraunces-spec;

  // prettier-ignore
  @include fluid(font-size, (xxs: 2.8rem, xxl: 10rem));

  margin: 0;
  line-height: 1;
}

.legacy__section {
  &:nth-child(1) {
    color: $c-white;
    background-color: $c-green-dark;

    .legacy__section__title {
      color: $c-green-light;
    }

    :deep(.duo-gallery__content__title) {
      color: $c-white;
    }
  }

  &:nth-child(2) {
    color: $c-white;
    background-color: $c-brown-very-dark;

    .legacy__section__title {
      color: $c-brown-medium;
    }

    :deep(.duo-gallery__content__title) {
      color: $c-white;
    }
  }

  &:nth-child(3) {
    color: $c-white;
    background-color: $c-brown-dark;

    .legacy__section__title {
      color: $c-brown-light;
    }

    :deep(.duo-gallery__content__title) {
      color: $c-white;
    }
  }

  &:nth-child(4) {
    background-color: $c-beige;
  }

  &:nth-child(5) {
    // prettier-ignore
    @include fluid(padding-top, (xxs: 6.4rem, xxl: 12rem));

    background-color: $c-white;

    :deep(.title__container) {
      margin-top: 0;
    }
  }

  :deep(.duo-gallery__content__title) {
    @extend %fraunces-spec;
    @extend %fw-light;
  }
}
</style>
